import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonService } from '../../../../../../../../../../shared/services/common.service';
import * as fromRoot from '../../../../../../../../../../state/app.state';
import { appDataDetails } from '../../../../../../../../../../shared/core/shared.selectors';
import { isSampleCollectionEnabled } from '../../../../../../../../../../shared/helpers/sample-collection';
import { currentLocation, projectDetails, siteDetails } from '../../../../../../../../core/projects.selectors';
import { ItemPostingsStationPopupComponent } from '../item-postings-station-popup/item-postings-station-popup.component';
import { ItemPostingsMaterialSetDialogComponent } from '../item-postings-material-set-dialog/item-postings-material-set-dialog.component';
import { ItemPostingsAcceptanceRecordDialogComponent } from '../item-postings-acceptance-record-dialog/item-postings-acceptance-record-dialog.component';
import { ItemPostingsCustomGroupDialogComponent } from '../item-postings-custom-group-dialog/item-postings-custom-group-dialog.component';
import { acceptanceRecordHasData, parseAcceptanceGroupValues } from '../acceptance-record.utils';
import { LocationPickerComponent } from '../../../../../../../../../../shared/components/location-picker/location-picker.component';
import { DailyReportService } from '../../../../daily-report.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../../shared/components';

@Component({
  standalone: false,
  selector: 'app-item-posting-line-item',
  templateUrl: './item-posting-line-item.component.html',
  styleUrls: ['./item-posting-line-item.component.scss'],
})
export class ItemPostingLineItemComponent implements OnInit, OnChanges, OnDestroy {
  @Input() lineItem: any;
  @Input() form: FormGroup;
  @Input() contractorOptions: any[] = [];
  @Input() showAddContractorButton = false;
  @Input() isExpanded = false;
  @Input() uom = '';
  @Input() quantityRounding = 2;
  @Input() materialSets: any[] = [];
  @Input() bidItemName = '';
  @Input() areaCalculationGroups: any[] = [];
  @Input() projectLookups: any = {};
  // Plumbed in from item-postings-details so the embedded Sample Records
  // affordance can scope its list/save to (daily_report_id, bid_item_material_set_id).
  @Input() projectId: string;
  @Input() dailyReportId: number | string;
  @Input() bidItemId: number | string;
  @Input() editable = false;
  // True when the org's appData exposes sample_record_general fields. Lets
  // the template gate the embedded affordance the same way the standalone
  // Sample Records tab is gated.
  sampleRecordsEnabled = false;

  @Output() toggleExpand = new EventEmitter<void>();
  @Output() addContractorClicked = new EventEmitter<void>();
  @Output() deleteClicked = new EventEmitter<void>();

  stationRange: any = null;
  project: any = null;
  appData: any = null;
  mediaFiles: any[] = [];
  materialSetData: any = null; // { materialSetId, materialSetName, materials }
  areaCalcData: any = null; // current area calculation group_values for this line item
  stationRangeMapData: any = null;
  showLocationStationError = false;
  errorMessages: { [key: string]: string } = {};
  private stationRangeMapDataKey = '';
  errorMessageMap = {
    contractor: {
      required: 'Contractor is required',
    },
  };

  get hasLocationOrStation(): boolean {
    return !!(
      this.currentStationRange
      || this.form?.controls['location']?.value
      || (this.form?.controls['latitude']?.value && this.form?.controls['longitude']?.value)
    );
  }

  get currentStationRange(): any {
    const formOwnsStationRange = !!this.form?.controls['stationRangeFromStation'];
    return this.normalizeStationRange(formOwnsStationRange
      ? this.buildStationRangeFromForm()
      : this.stationRange || this.lineItem?.station_range);
  }
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private dialog: MatDialog,
    private store: Store<fromRoot.State>,
    private dailyReportService: DailyReportService,
    private cdRef: ChangeDetectorRef,
    private commonService: CommonService,
    private elRef: ElementRef,
  ) {}

  ngOnInit() {
    // Watch for picture/video form changes to update media files
    this.form.controls['pictures']?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.updateMediaFiles());

    this.form.controls['videos']?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.updateMediaFiles());

    this.updateMediaFiles();

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.project = project;
        this.refreshSampleRecordsEnabled();
        this.refreshStationRangeMapData();
        this.cdRef.detectChanges();
      });

    this.store.select(appDataDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(appData => {
        this.appData = appData;
        this.refreshSampleRecordsEnabled();
      });

    // Add station range controls to the form (without emitting to avoid marking dirty)
    if (!this.form.controls['stationRangeFromStation']) {
      this.form.addControl('alignment', new FormControl(null), { emitEvent: false });
      this.form.addControl('stationRangeFromStation', new FormControl(null), { emitEvent: false });
      this.form.addControl('stationRangeFromOffsetType', new FormControl(null), { emitEvent: false });
      this.form.addControl('stationRangeFromOffsetDistance', new FormControl(null), { emitEvent: false });
      this.form.addControl('stationRangeToStation', new FormControl(null), { emitEvent: false });
      this.form.addControl('stationRangeToOffsetType', new FormControl(null), { emitEvent: false });
      this.form.addControl('stationRangeToOffsetDistance', new FormControl(null), { emitEvent: false });
    } else if (!this.form.controls['alignment']) {
      this.form.addControl('alignment', new FormControl(null), { emitEvent: false });
    }
    this.watchStationRangeMapInputs();

    this.dailyReportService.setStationRange
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data?.form && data.form !== this.form) return;
        this.stationRange = data?.stationRange;
        this.refreshStationRangeMapData();
        if (this.hasLocationOrStation) this.showLocationStationError = false;
        this.cdRef.detectChanges();
      });

    // Clear contractor error when contractor is selected
    this.form.controls['contractor']?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.checkForErrors('contractor');
      });

    // Clear location/station error when location is set
    this.form.controls['latitude']?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.refreshStationRangeMapData();
        if (this.hasLocationOrStation) this.showLocationStationError = false;
      });

    // Restore materialSetData from existing form data
    this.restoreExistingData();

    this.refreshStationRangeMapData();
  }

  // Mirror the standalone Sample Records tab gating: only render the embedded
  // affordance when the org has sample_record_general configured and, when
  // the org rolls sample collection out per project, this project is opted in.
  private refreshSampleRecordsEnabled() {
    this.sampleRecordsEnabled = isSampleCollectionEnabled(this.appData, this.project);
  }

  private watchStationRangeMapInputs() {
    [
      'alignment',
      'stationRangeFromStation',
      'stationRangeFromOffsetType',
      'stationRangeFromOffsetDistance',
      'stationRangeToStation',
      'stationRangeToOffsetType',
      'stationRangeToOffsetDistance',
      'longitude',
      'location',
    ].forEach(controlName => {
      this.form.controls[controlName]?.valueChanges
        .pipe(takeUntil(this.onDestroy))
        .subscribe(() => {
          this.refreshStationRangeMapData();
        });
    });
  }

  private restoreExistingData() {
    // Restore material set data
    const materialsJson = this.form.controls['materials']?.value;
    if (materialsJson) {
      const materials = JSON.parse(materialsJson);
      if (materials?.length > 0) {
        const firstMat = materials[0];
        const materialSetId = firstMat.bid_item_material_set?.id || firstMat.bid_item_material_set_id;
        const materialSetName = firstMat.bid_item_material_set?.name || '';
        if (materialSetId || firstMat.bid_item_material_set) {
          this.materialSetData = {
            materialSetId,
            materialSetName,
            materials,
          };
        }
      }
    }

    // Restore station range for display
    const fromStation = this.form.controls['stationRangeFromStation']?.value;
    const toStation = this.form.controls['stationRangeToStation']?.value;
    const alignment = this.form.controls['alignment']?.value || this.lineItem?.station_range?.alignment;
    if (fromStation || toStation || alignment) {
      this.stationRange = {
        alignment,
        from: {
          station: fromStation,
          offsetType: this.form.controls['stationRangeFromOffsetType']?.value,
          offsetDistance: this.form.controls['stationRangeFromOffsetDistance']?.value,
        },
        to: {
          station: toStation,
          offsetType: this.form.controls['stationRangeToOffsetType']?.value,
          offsetDistance: this.form.controls['stationRangeToOffsetDistance']?.value,
        },
      };
    }
    this.refreshStationRangeMapData();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Auto-apply when materialSets input arrives
    if (changes['materialSets']) {
      this.autoApplyMaterialSet();
    }

    // Mirror bid items: a line item the user may not save is not editable.
    // Parent totals read control values directly, so a disabled form still
    // reports its quantity.
    if ((changes['editable'] || changes['form']) && this.form) {
      if (this.editable) {
        this.form.enable({ emitEvent: false });
      } else {
        this.form.disable({ emitEvent: false });
      }
    }

    if (changes['lineItem'] || changes['form']) {
      this.refreshStationRangeMapData();
    }

    // Scroll into view when expanded
    if (changes['isExpanded']?.currentValue && !changes['isExpanded']?.previousValue) {
      setTimeout(() => {
        this.elRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }

  deletePicture(pic: any) {
    const pictures = JSON.parse(this.form.controls['pictures']?.value || '[]');
    const videos = JSON.parse(this.form.controls['videos']?.value || '[]');
    const updatedPictures = pictures.filter(p => p.id !== pic.id);
    const updatedVideos = videos.filter(v => v.id !== pic.id);
    this.form.controls['pictures'].setValue(JSON.stringify(updatedPictures));
    this.form.controls['videos'].setValue(JSON.stringify(updatedVideos));
    this.updateMediaFiles();
  }

  deleteLocation() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Clear location',
        'Are you sure you want to clear this location and station range?',
        'Clear',
        'Cancel',
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.clearLocation();
      }
    });
  }

  private clearLocation() {
    this.form.controls['latitude']?.setValue(null);
    this.form.controls['longitude']?.setValue(null);
    this.form.controls['location']?.setValue(null);
    this.form.controls['alignment']?.setValue(null);
    this.form.controls['stationRangeFromStation']?.setValue(null);
    this.form.controls['stationRangeFromOffsetType']?.setValue(null);
    this.form.controls['stationRangeFromOffsetDistance']?.setValue(null);
    this.form.controls['stationRangeToStation']?.setValue(null);
    this.form.controls['stationRangeToOffsetType']?.setValue(null);
    this.form.controls['stationRangeToOffsetDistance']?.setValue(null);
    this.stationRange = null;
    this.refreshStationRangeMapData();
  }

  private normalizeStationRange(stationRange: any) {
    if (!stationRange) return null;

    const from = stationRange.from || {};
    const to = stationRange.to || {};
    const normalized = {
      alignment: stationRange.alignment || null,
      from: {
        station: from.station || null,
        offsetType: from.offsetType ?? from.offset_type ?? null,
        offsetDistance: from.offsetDistance ?? from.offset_distance ?? null,
      },
      to: {
        station: to.station || null,
        offsetType: to.offsetType ?? to.offset_type ?? null,
        offsetDistance: to.offsetDistance ?? to.offset_distance ?? null,
      },
    };

    const hasValue = [
      normalized.alignment,
      normalized.from.station,
      normalized.from.offsetType,
      normalized.from.offsetDistance,
      normalized.to.station,
      normalized.to.offsetType,
      normalized.to.offsetDistance,
    ].some(value => value !== null && value !== undefined && value !== '');

    return hasValue ? normalized : null;
  }

  private buildStationRangeFromForm() {
    const alignment = this.form?.controls['alignment']?.value;
    const from = {
      station: this.form?.controls['stationRangeFromStation']?.value,
      offsetType: this.form?.controls['stationRangeFromOffsetType']?.value,
      offsetDistance: this.form?.controls['stationRangeFromOffsetDistance']?.value,
    };
    const to = {
      station: this.form?.controls['stationRangeToStation']?.value,
      offsetType: this.form?.controls['stationRangeToOffsetType']?.value,
      offsetDistance: this.form?.controls['stationRangeToOffsetDistance']?.value,
    };

    const hasValue = [
      alignment,
      from.station,
      from.offsetType,
      from.offsetDistance,
      to.station,
      to.offsetType,
      to.offsetDistance,
    ].some(value => value !== null && value !== undefined && value !== '');

    return hasValue ? { alignment, from, to } : null;
  }

  private refreshStationRangeMapData() {
    const stationRange = this.currentStationRange;
    const alignmentGeometry = this.project?.alignment_geometry || this.project?.alignmentGeometry || null;
    const locationPoint = this.getLocationPointFromForm();
    const cacheKey = JSON.stringify({
      stationRange,
      alignmentGeometry,
      locationPoint,
    });

    if (cacheKey === this.stationRangeMapDataKey) return;

    this.stationRangeMapDataKey = cacheKey;
    this.stationRangeMapData = this.buildStationRangeMapData(stationRange, alignmentGeometry, locationPoint);
  }

  private buildStationRangeMapData(stationRange: any, alignmentGeometry: any, locationPoint: any = null) {
    if (!stationRange) return null;

    const alignments = this.parseAlignmentGeometry(alignmentGeometry);
    if (!alignments.length) return null;

    const alignment = stationRange.alignment
      ? alignments.find(a => a.name === stationRange.alignment)
      : alignments[0];
    if (!alignment) return null;

    const selectedPath = this.getStationRangePolyline({
      alignment,
      fromStationName: stationRange.from?.station,
      toStationName: stationRange.to?.station,
    });
    if (selectedPath.length < 2) return null;

    const fromOffsetPoint = this.hasNonZeroOffset(stationRange.from)
      ? this.getOffsetStationPosition({
        alignment,
        stationName: stationRange.from?.station,
        offsetType: stationRange.from?.offsetType,
        offsetDistance: stationRange.from?.offsetDistance,
      })
      : null;
    const toOffsetPoint = this.hasNonZeroOffset(stationRange.to)
      ? this.getOffsetStationPosition({
        alignment,
        stationName: stationRange.to?.station,
        offsetType: stationRange.to?.offsetType,
        offsetDistance: stationRange.to?.offsetDistance,
      })
      : null;

    const endCapPaths = this.getStationRangeEndCapPaths(selectedPath[0], selectedPath[selectedPath.length - 1]);

    return {
      center: this.getMapCenter(selectedPath),
      geometry: this.buildLineStringFeatureCollection(
        alignment.polylineCoordinates,
        selectedPath,
        locationPoint,
        fromOffsetPoint,
        toOffsetPoint,
      ),
      selectedPath,
      endCapPaths,
      fromPosition: selectedPath[0],
      toPosition: selectedPath[selectedPath.length - 1],
      fromOffsetPoint,
      toOffsetPoint,
      fromLabel: stationRange.from?.station,
      toLabel: stationRange.to?.station,
      zoom: this.getStationRangeMapZoom(selectedPath),
    };
  }

  private hasNonZeroOffset(side: any) {
    const type = `${side?.offsetType || ''}`.toUpperCase();
    return (type.startsWith('RT') || type.startsWith('LT')) && Number(side?.offsetDistance) > 0;
  }

  private parseStationToFeet(stationName: string): number | null {
    const match = `${stationName || ''}`.match(/^(\d+)\+(\d+)$/);
    if (!match) return null;
    return parseInt(match[1], 10) * 100 + parseInt(match[2], 10);
  }

  private getOffsetDirectionMultiplier(offsetType: string) {
    if (`${offsetType || ''}`.startsWith('RT')) return -1;
    if (`${offsetType || ''}`.startsWith('LT')) return 1;
    return 0;
  }

  private getSignedOffsetFeet({ offsetType, offsetDistance }) {
    const distance = parseFloat(offsetDistance);
    return this.getOffsetDirectionMultiplier(offsetType) * (Number.isFinite(distance) ? distance : 0);
  }

  private getDistanceBetweenCoordinatesFeet(start: any, end: any) {
    if (!start || !end) return 0;
    const earthRadiusFeet = 20902231;
    const toRad = deg => (deg * Math.PI) / 180;
    const latitude = (start.lat + end.lat) / 2;
    const cosLat = Math.max(Math.cos(toRad(latitude)), 0.000001);
    const dx = toRad(end.lng - start.lng) * earthRadiusFeet * cosLat;
    const dy = toRad(end.lat - start.lat) * earthRadiusFeet;
    return Math.sqrt(dx * dx + dy * dy);
  }

  private getOffsetCoordinate({ coordinate, previousCoordinate, nextCoordinate, offsetFeet }) {
    if (!coordinate || !offsetFeet) return coordinate;
    const tangentStart = previousCoordinate || coordinate;
    const tangentEnd = nextCoordinate || coordinate;
    const earthRadiusFeet = 20902231;
    const toRad = deg => (deg * Math.PI) / 180;
    const toDeg = rad => (rad * 180) / Math.PI;
    const cosLat = Math.max(Math.cos(toRad(coordinate.lat)), 0.000001);
    const dx = toRad(tangentEnd.lng - tangentStart.lng) * earthRadiusFeet * cosLat;
    const dy = toRad(tangentEnd.lat - tangentStart.lat) * earthRadiusFeet;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (!length) return coordinate;

    const leftNormalX = -dy / length;
    const leftNormalY = dx / length;
    const offsetX = leftNormalX * offsetFeet;
    const offsetY = leftNormalY * offsetFeet;

    return {
      lat: coordinate.lat + toDeg(offsetY / earthRadiusFeet),
      lng: coordinate.lng + toDeg(offsetX / (earthRadiusFeet * cosLat)),
    };
  }

  private getOffsetStationRangePolyline({
    polyline,
    fromOffsetType,
    fromOffsetDistance,
    toOffsetType,
    toOffsetDistance,
  }) {
    if (!polyline?.length || polyline.length < 2) return polyline || [];

    const fromOffsetFeet = this.getSignedOffsetFeet({ offsetType: fromOffsetType, offsetDistance: fromOffsetDistance });
    const toOffsetFeet = this.getSignedOffsetFeet({ offsetType: toOffsetType, offsetDistance: toOffsetDistance });
    if (!fromOffsetFeet && !toOffsetFeet) return polyline;

    const segmentLengths = polyline
      .slice(0, -1)
      .map((coordinate, index) => this.getDistanceBetweenCoordinatesFeet(coordinate, polyline[index + 1]));
    const totalLength = segmentLengths.reduce((sum, length) => sum + length, 0);
    let cumulativeLength = 0;

    return polyline.map((coordinate, index) => {
      if (index > 0) {
        cumulativeLength += segmentLengths[index - 1] || 0;
      }

      const progress = totalLength ? cumulativeLength / totalLength : 0;
      const offsetFeet = fromOffsetFeet + (toOffsetFeet - fromOffsetFeet) * progress;
      return this.getOffsetCoordinate({
        coordinate,
        previousCoordinate: polyline[index - 1],
        nextCoordinate: polyline[index + 1],
        offsetFeet,
      });
    });
  }

  private getStationPositionOnAlignment({ alignment, stationName }) {
    const stationFeet = this.parseStationToFeet(stationName);
    const stations = alignment?.stations || [];
    if (stationFeet == null || stations.length < 2) return null;

    for (let index = 0; index < stations.length - 1; index++) {
      const segmentStartFeet = this.parseStationToFeet(stations[index].name);
      const segmentEndFeet = this.parseStationToFeet(stations[index + 1].name);
      const segmentStart = stations[index].coordinate;
      const segmentEnd = stations[index + 1].coordinate;

      if (segmentStartFeet == null || segmentEndFeet == null || !segmentStart || !segmentEnd) {
        continue;
      }

      const segmentLow = Math.min(segmentStartFeet, segmentEndFeet);
      const segmentHigh = Math.max(segmentStartFeet, segmentEndFeet);
      if (stationFeet < segmentLow || stationFeet > segmentHigh) {
        continue;
      }

      const ratio = segmentEndFeet === segmentStartFeet
        ? 0
        : (stationFeet - segmentStartFeet) / (segmentEndFeet - segmentStartFeet);

      return {
        coordinate: {
          lat: segmentStart.lat + ratio * (segmentEnd.lat - segmentStart.lat),
          lng: segmentStart.lng + ratio * (segmentEnd.lng - segmentStart.lng),
        },
        previousCoordinate: segmentStart,
        nextCoordinate: segmentEnd,
      };
    }

    return null;
  }

  private getOffsetStationPosition({ alignment, stationName, offsetType, offsetDistance }) {
    const stationPosition = this.getStationPositionOnAlignment({ alignment, stationName });
    if (!stationPosition) return null;

    return this.getOffsetCoordinate({
      coordinate: stationPosition.coordinate,
      previousCoordinate: stationPosition.previousCoordinate,
      nextCoordinate: stationPosition.nextCoordinate,
      offsetFeet: this.getSignedOffsetFeet({ offsetType, offsetDistance }),
    });
  }

  private makeCoordinate(coordinate: any) {
    if (!coordinate) return null;
    if (Array.isArray(coordinate) && coordinate.length >= 2) {
      return { lat: Number(coordinate[1]), lng: Number(coordinate[0]) };
    }
    const lat = Number(coordinate.lat ?? coordinate.latitude);
    const lng = Number(coordinate.lng ?? coordinate.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
    return null;
  }

  private parseAlignmentGeometry(alignmentGeometry: any) {
    try {
      const geojson = typeof alignmentGeometry === 'string'
        ? JSON.parse(alignmentGeometry)
        : alignmentGeometry;
      if (!geojson?.features?.length) return [];

      return geojson.features.map(feature => {
        const coordinates = feature?.geometry?.coordinates || [];
        const rawStations = feature?.properties?.stations || [];
        const stations = rawStations.map((station, index) => {
          const coordinate = this.makeCoordinate(coordinates[index]);
          return coordinate ? { name: station?.name, coordinate } : null;
        }).filter(Boolean);

        return {
          name: feature?.properties?.name || '',
          polylineCoordinates: coordinates.map(coordinate => this.makeCoordinate(coordinate)).filter(Boolean),
          stations,
        };
      });
    } catch {
      return [];
    }
  }

  private getStationRangePolyline({ alignment, fromStationName, toStationName }) {
    const fromFeet = this.parseStationToFeet(fromStationName);
    const toFeet = this.parseStationToFeet(toStationName);
    const stations = alignment?.stations || [];

    if (fromFeet == null || toFeet == null || stations.length < 2 || !alignment?.polylineCoordinates?.length) {
      return [];
    }

    const rangeStart = Math.min(fromFeet, toFeet);
    const rangeEnd = Math.max(fromFeet, toFeet);
    const points = [];

    for (let index = 0; index < stations.length - 1; index++) {
      const segmentStartFeet = this.parseStationToFeet(stations[index].name);
      const segmentEndFeet = this.parseStationToFeet(stations[index + 1].name);
      const segmentStart = stations[index].coordinate;
      const segmentEnd = stations[index + 1].coordinate;

      if (segmentStartFeet == null || segmentEndFeet == null || !segmentStart || !segmentEnd) {
        continue;
      }

      const segmentLow = Math.min(segmentStartFeet, segmentEndFeet);
      const segmentHigh = Math.max(segmentStartFeet, segmentEndFeet);
      const overlapStart = Math.max(rangeStart, segmentLow);
      const overlapEnd = Math.min(rangeEnd, segmentHigh);

      if (overlapStart > overlapEnd) {
        continue;
      }

      const coordinateForFeet = feet => {
        const ratio = segmentEndFeet === segmentStartFeet
          ? 0
          : (feet - segmentStartFeet) / (segmentEndFeet - segmentStartFeet);
        return {
          lat: segmentStart.lat + ratio * (segmentEnd.lat - segmentStart.lat),
          lng: segmentStart.lng + ratio * (segmentEnd.lng - segmentStart.lng),
        };
      };

      const startCoordinate = coordinateForFeet(overlapStart);
      const endCoordinate = coordinateForFeet(overlapEnd);
      const lastPoint = points[points.length - 1];

      if (!lastPoint || lastPoint.lat !== startCoordinate.lat || lastPoint.lng !== startCoordinate.lng) {
        points.push(startCoordinate);
      }
      points.push(endCoordinate);
    }

    return points;
  }

  private getMapCenter(points: any[]) {
    const totals = points.reduce((acc, point) => ({
      lat: acc.lat + point.lat,
      lng: acc.lng + point.lng,
    }), { lat: 0, lng: 0 });

    return {
      lat: totals.lat / points.length,
      lng: totals.lng / points.length,
    };
  }

  private getStationRangeMapZoom(points: any[]) {
    const bounds = points.reduce((acc, point) => ({
      minLat: Math.min(acc.minLat, point.lat),
      maxLat: Math.max(acc.maxLat, point.lat),
      minLng: Math.min(acc.minLng, point.lng),
      maxLng: Math.max(acc.maxLng, point.lng),
    }), {
      minLat: points[0].lat,
      maxLat: points[0].lat,
      minLng: points[0].lng,
      maxLng: points[0].lng,
    });
    const span = Math.max(bounds.maxLat - bounds.minLat, bounds.maxLng - bounds.minLng);

    if (span < 0.0003) return 19;
    if (span < 0.0008) return 18;
    return 17;
  }

  private getStationRangeEndCapPaths(start: any, end: any) {
    if (!start || !end) return [];

    const latDelta = end.lat - start.lat;
    const lngDelta = end.lng - start.lng;
    const length = Math.sqrt(latDelta * latDelta + lngDelta * lngDelta);
    if (!length) return [];

    const perpendicularLat = -lngDelta / length;
    const perpendicularLng = latDelta / length;
    const capLength = Math.max(length * 0.16, 0.000055);
    const halfCapLength = capLength / 2;

    const capForPoint = point => [
      {
        lat: point.lat - perpendicularLat * halfCapLength,
        lng: point.lng - perpendicularLng * halfCapLength,
      },
      {
        lat: point.lat + perpendicularLat * halfCapLength,
        lng: point.lng + perpendicularLng * halfCapLength,
      },
    ];

    return [capForPoint(start), capForPoint(end)];
  }

  private getLocationPointFromForm() {
    const latitudeValue = this.form?.controls['latitude']?.value;
    const longitudeValue = this.form?.controls['longitude']?.value;
    if (!this.hasCoordinateValue(latitudeValue) || !this.hasCoordinateValue(longitudeValue)) {
      return null;
    }

    const latitude = Number(latitudeValue);
    const longitude = Number(longitudeValue);
    return Number.isFinite(latitude) && Number.isFinite(longitude)
      ? { lat: latitude, lng: longitude }
      : null;
  }

  private hasCoordinateValue(value: any) {
    return value !== null && value !== undefined && `${value}`.trim() !== '';
  }

  private buildLineStringFeatureCollection(
    alignmentPath: any[],
    selectedPath: any[] = [],
    locationPoint: any = null,
    fromOffsetPoint: any = null,
    toOffsetPoint: any = null,
  ) {
    const features = [];
    if (alignmentPath?.length >= 2) {
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: alignmentPath.map(point => [point.lng, point.lat]),
          color: selectedPath?.length >= 2 ? '#8EA0D8' : '#1036D6',
          opacity: selectedPath?.length >= 2 ? 0.55 : 1,
          strokeWidth: selectedPath?.length >= 2 ? 2 : 3,
          includeInExtent: selectedPath?.length < 2,
        },
      });
    }

    if (selectedPath?.length >= 2) {
      // Card mini-map: dashed range + book-end ticks (mobile parity). Coarser
      // dash + thinner stroke so it reads as dashed at the small card zoom.
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: selectedPath.map(point => [point.lng, point.lat]),
          color: '#1036D6',
          dashTemplate: [12, 9],
          strokeWidth: 3,
        },
      });

      this.getStationRangeEndCapPaths(selectedPath[0], selectedPath[selectedPath.length - 1]).forEach(capPath => {
        features.push({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: capPath.map(point => [point.lng, point.lat]),
            color: '#1036D6',
            strokeWidth: 3,
          },
        });
      });
    }

    if (locationPoint) {
      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Point',
          coordinates: [locationPoint.lng, locationPoint.lat],
          color: '#1036D6',
          markerIconUrl: '/assets/icons/map/markers/location-pin.svg',
          iconSize: { width: 14, height: 19, yOffset: 9.5 },
          includeInExtent: true,
        },
      });
    }

    return {
      type: 'FeatureCollection',
      features,
    };
  }

  private updateMediaFiles() {
    const pictures = JSON.parse(this.form.controls['pictures']?.value || '[]')
      .map(p => ({ ...p, fileType: 'picture' }));
    const videos = JSON.parse(this.form.controls['videos']?.value || '[]')
      .map(v => ({ ...v, fileType: 'video' }));
    this.mediaFiles = [...pictures, ...videos];
  }

  openLocationPicker() {
    const formLat = this.form?.controls['latitude']?.value;
    const formLng = this.form?.controls['longitude']?.value;

    if (formLat && formLng) {
      this.dialog.open(LocationPickerComponent, {
        disableClose: true,
        data: {
          latitude: formLat,
          longitude: formLng,
          readOnly: !this.editable,
          enableLocationSection: true,
          showIncludeGpsLocationCheckbox: !!this.appData?.enableAashtoIntegration,
          locationText: this.form?.controls['location']?.value || '',
        },
      });
      return;
    }

    this.store.select(currentLocation)
      .subscribe(location => {
        let loc = location;
        if (!loc?.latitude || !loc?.longitude) {
          this.store.select(siteDetails)
            .subscribe(site => {
              if (site?.longitude && site?.latitude) {
                loc = { latitude: site?.latitude, longitude: site?.longitude };
              } else {
                this.store.select(projectDetails)
                  .subscribe(project => {
                    if (project?.longitude && project?.latitude) {
                      loc = { latitude: project?.latitude, longitude: project?.longitude };
                    }
                  }).unsubscribe();
              }
            }).unsubscribe();
        }
        this.dialog.open(LocationPickerComponent, {
          disableClose: true,
          data: {
            ...loc,
            readOnly: !this.editable,
            enableLocationSection: true,
            showIncludeGpsLocationCheckbox: !!this.appData?.enableAashtoIntegration,
            locationText: this.form?.controls['location']?.value || '',
          },
        });
      }).unsubscribe();
  }

  openStationPopup() {
    this.dialog.open(ItemPostingsStationPopupComponent, {
      disableClose: true,
      width: '1200px',
      height: '620px',
      maxWidth: 'calc(100vw - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      panelClass: 'station-range-dialog-panel',
      data: {
        form: this.form,
        stationRange: this.currentStationRange,
      },
    });
  }

  private autoApplyMaterialSet() {
    if (this.materialSets?.length !== 1 || this.materialSetData) return;

    // Preserve group_values from existing form materials (API data)
    let existingMaterials: any[] = [];
    try {
      existingMaterials = JSON.parse(this.form.controls['materials']?.value || '[]');
    } catch { }

    const set = this.materialSets[0];
    const lineItemQty = parseFloat(this.form.controls['quantity']?.value) || 0;

    const materials = set.bid_item_material_set_material?.map(msm => {
      // Find matching existing material to preserve group_values
      const existing = existingMaterials.find(m =>
        (m.bid_item_material_set_material_id || m.bid_item_material_set_material?.id) === msm.id
      );

      return {
        quantity: existing?.quantity ?? (lineItemQty * (parseFloat(msm.conversion_factor) || 0)),
        bid_item_material_set: { id: set.id, name: set.name },
        bid_item_material_set_id: set.id,
        bid_item_material_set_material: { id: msm.id, conversion_factor: msm.conversion_factor },
        bid_item_material_set_material_id: msm.id,
        material: msm.material,
        material_id: msm.material?.id,
        ...(existing?.group_values ? { group_values: existing.group_values } : {}),
      };
    }) || [];

    this.materialSetData = {
      materialSetId: set.id,
      materialSetName: set.name,
      materials,
    };
    this.form.controls['materialSetId']?.setValue(set.id, { emitEvent: false });
    this.form.controls['materials']?.setValue(JSON.stringify(materials), { emitEvent: false });
  }

  hasAcceptanceRecordData(): boolean {
    return this.materialSetData?.materials?.some(m => {
      const { records } = parseAcceptanceGroupValues(m.group_values || m.material?.group_values);
      return records.some(acceptanceRecordHasData);
    }) || false;
  }

  // Memoized per material by group_values identity — the getter is called from
  // an @let on every change-detection pass, and parseAcceptanceGroupValues
  // does JSON.parse + uuid generation each time it runs.
  private acceptanceSummariesCache: {
    [materialKey: string]: { source: any; summaries: { index: number; text: string }[] };
  } = {};

  // One summary per acceptance record that has data (legacy single-record
  // shape normalizes to one entry). `index` is the record's position in the
  // full records array (before empty records are filtered out) so card labels
  // match the dialog's "Record N" numbering.
  getAcceptanceRecordSummaries(material: any): { index: number; text: string }[] {
    const materialKey = material.bid_item_material_set_material_id
      || material.bid_item_material_set_material?.id
      || material.material_id
      || material.material?.id;
    const source = material.group_values || material.material?.group_values;
    const cached = this.acceptanceSummariesCache[materialKey];
    if (cached && cached.source === source) return cached.summaries;

    const { records } = parseAcceptanceGroupValues(source);
    const summaries = records
      .map((record, index) => ({
        index,
        text: [
          ...(record.acceptance_record || []),
          ...(record.groups || []),
        ].filter(v => v.value != null && v.value !== '')
         .map(v => typeof v.value === 'object' ? (v.value?.label || v.value?.name || v.value?.value || '') : v.value)
         .filter(v => v !== '')
         .join(', '),
      }))
      .filter(summary => summary.text !== '');

    this.acceptanceSummariesCache[materialKey] = { source, summaries };
    return summaries;
  }

  openAcceptanceRecordDialog() {
    const dialogRef = this.dialog.open(ItemPostingsAcceptanceRecordDialogComponent, {
      disableClose: true,
      width: '600px',
      maxHeight: '90vh',
      data: {
        materials: this.materialSetData?.materials || [],
        bidItemName: this.bidItemName,
        projectLookups: this.projectLookups,
        projectId: this.projectId,
        dailyReportId: this.dailyReportId,
        bidItemId: this.bidItemId,
        bidItemMaterialSetId: this.materialSetData?.materialSetId,
        sampleRecordsEnabled: this.sampleRecordsEnabled,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      if (result.deleted) {
        this.clearAcceptanceRecordData();
      } else if (result.materials) {
        this.materialSetData = {
          ...this.materialSetData,
          materials: result.materials,
        };
      }
      this.form.controls['materials']?.setValue(JSON.stringify(this.materialSetData.materials));
      this.cdRef.detectChanges();
    });
  }

  openMaterialSetDialog() {
    const dialogRef = this.dialog.open(ItemPostingsMaterialSetDialogComponent, {
      disableClose: true,
      width: '600px',
      data: {
        materialSets: this.materialSets,
        lineItemQuantity: parseFloat(this.form.controls['quantity']?.value) || 0,
        bidItemName: this.bidItemName,
        uom: this.uom,
        selectedMaterialSetId: this.materialSetData?.materialSetId || null,
        materials: this.materialSetData?.materials || [],
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      if (result.deleted) {
        this.materialSetData = null;
        this.form.controls['materials']?.setValue(JSON.stringify([]));
        this.form.controls['materialSetId']?.setValue(null);
      } else {
        this.materialSetData = result;
        this.form.controls['materials']?.setValue(JSON.stringify(result.materials));
        this.form.controls['materialSetId']?.setValue(result.materialSetId);
      }
    });
  }

  hasAreaCalculationData(): boolean {
    const groupValues = JSON.parse(this.form.controls['groupValues']?.value || '[]');
    return this.areaCalculationGroups.some(g =>
      groupValues.some(gv => gv?.context === g.context)
    );
  }

  getAreaCalculationCardInfo(group: any): { title: string; description: string }[] {
    const groupValues = JSON.parse(this.form.controls['groupValues']?.value || '[]');
    const gv = groupValues.find(v => v?.context === group.context);
    if (!gv) return [];

    const customFields = [...(group.fields || [])].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

    // Get rows from field_values or multi_line_field_values
    const fieldValues = gv.field_values || [];
    const multiLineFieldValues = gv.multi_line_field_values || [];

    const info: { title: string; description: string }[] = [];

    // Helper to format value (dates and times get formatted)
    const fmt = (val: any): string => {
      if (val == null || val === '') return '';
      if (typeof val === 'object') return val.label || val.name || val.value || '';
      if (typeof val === 'string') {
        // Date format: "2026-03-01T..."
        if (/^\d{4}-\d{2}-\d{2}T/.test(val)) {
          const d = new Date(val);
          if (!isNaN(d.getTime())) {
            return d.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
          }
        }
        // 24-hour time format: "18:59:00" or "18:59"
        const timeMatch = val.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
        if (timeMatch) {
          let hours = parseInt(timeMatch[1], 10);
          const minutes = timeMatch[2];
          const amPm = hours >= 12 ? 'PM' : 'AM';
          if (hours === 0) hours = 12;
          else if (hours > 12) hours -= 12;
          return `${String(hours).padStart(2, '0')}:${minutes} ${amPm}`;
        }
      }
      return String(val);
    };

    // Build card rows from field values
    const buildRows = (rows: any[][]) => {
      for (const row of rows) {
        if (!Array.isArray(row)) continue;
        const fvDict: { [id: string]: any } = {};
        row.forEach(fv => { if (fv?.id) fvDict[fv.id] = fv.value; });

        const titleField = customFields[0];
        const title = titleField ? (fmt(fvDict[titleField.id]) || titleField.label || '') : '';
        const otherParts = customFields.slice(1)
          .filter(f => fvDict[f.id] != null && fvDict[f.id] !== '')
          .map(f => `${f.label}: ${fmt(fvDict[f.id])}`);
        info.push({ title, description: otherParts.join(' \u2022 ') });
      }
    };

    // Show multi-line rows
    if (multiLineFieldValues.length) {
      buildRows(multiLineFieldValues);
      return info;
    }

    // Check for section multi-line (2D field_values with mixed sections)
    const sections = group.ui_config?.sections || [];
    const hasSectionMultiLine = sections.some(s => s.multi_line);

    if (hasSectionMultiLine && Array.isArray(fieldValues) && fieldValues.length > 0 && Array.isArray(fieldValues[0])) {
      const singleLineSections = sections.filter(s => !s.multi_line);
      const multiLineSections = sections.filter(s => s.multi_line);

      // First row = single-line section fields, grouped by section
      const singleLineRow = fieldValues[0] || [];
      if (Array.isArray(singleLineRow) && singleLineRow.length) {
        const fvDict: { [id: string]: any } = {};
        singleLineRow.forEach(fv => { if (fv?.id) fvDict[fv.id] = fv.value; });

        for (const section of singleLineSections) {
          const sectionFields = customFields.filter(f => f.ui_config?.section_name === section.name);
          const parts = sectionFields
            .filter(f => fvDict[f.id] != null && fvDict[f.id] !== '')
            .map(f => `${f.label}: ${fmt(fvDict[f.id])}`);
          if (parts.length) {
            info.push({ title: section.label || section.name, description: parts.join(' \u2022 ') });
          }
        }
      }

      // Multi-line section rows
      const multiLineFields = customFields.filter(f =>
        multiLineSections.some(s => s.name === f.ui_config?.section_name)
      );
      const multiLineFieldIds = new Set(multiLineFields.map(f => f.id));
      const mlSectionLabel = multiLineSections[0]?.label || 'Details';

      // First density row is mixed into fieldValues[0]
      if (Array.isArray(singleLineRow)) {
        const fvDict: { [id: string]: any } = {};
        singleLineRow.forEach(fv => { if (fv?.id) fvDict[fv.id] = fv.value; });
        const parts = multiLineFields
          .filter(f => fvDict[f.id] != null && fvDict[f.id] !== '')
          .map(f => `${f.label}: ${fmt(fvDict[f.id])}`);
        if (parts.length) {
          info.push({ title: mlSectionLabel, description: parts.join(' \u2022 ') });
        }
      }

      // Additional density rows from fieldValues[1..n]
      for (let i = 1; i < fieldValues.length; i++) {
        const row = fieldValues[i];
        if (!Array.isArray(row)) continue;
        const fvDict: { [id: string]: any } = {};
        row.forEach(fv => { if (fv?.id) fvDict[fv.id] = fv.value; });

        const parts = multiLineFields
          .filter(f => fvDict[f.id] != null && fvDict[f.id] !== '')
          .map(f => `${f.label}: ${fmt(fvDict[f.id])}`);
        if (parts.length) {
          info.push({ title: mlSectionLabel, description: parts.join(' \u2022 ') });
        }
      }

      return info;
    }

    // Show single-line or section field_values
    if (Array.isArray(fieldValues) && fieldValues.length > 0) {
      const rows = Array.isArray(fieldValues[0]) ? fieldValues : [fieldValues];
      buildRows(rows);
    }

    return info;
  }

  openAreaCalculationDialog(group: any) {
    const existingGroupValues = JSON.parse(this.form.controls['groupValues']?.value || '[]');

    const dialogRef = this.dialog.open(ItemPostingsCustomGroupDialogComponent, {
      disableClose: true,
      width: '600px',
      maxHeight: '90vh',
      data: {
        areaCalculationGroup: group,
        bidItemName: this.bidItemName,
        existingGroupValues,
        projectLookups: this.projectLookups,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      this.form.controls['groupValues']?.setValue(JSON.stringify(result.groupValues || []));

      if (result.quantityUpdate?.shouldUpdate && result.quantityUpdate.value != null) {
        this.form.controls['quantity']?.setValue(result.quantityUpdate.value);
      }
    });
  }

  deleteMaterialSet() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Warning',
        'Are you sure you want to delete this entry?',
        'Delete',
        'Cancel',
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.materialSetData = null;
        this.form.controls['materialSetId']?.setValue(null);
        this.form.controls['materials']?.setValue(JSON.stringify([]));
      }
    });
  }

  deleteAcceptanceRecord() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Warning',
        'Are you sure you want to delete this entry?',
        'Delete',
        'Cancel',
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.clearAcceptanceRecordData();
        this.form.controls['materials']?.setValue(JSON.stringify(this.materialSetData?.materials || []));
        this.cdRef.detectChanges();
      }
    });
  }

  // Removes acceptance record data from every material while preserving any
  // other group_values keys (e.g. sample record data).
  private clearAcceptanceRecordData() {
    if (!this.materialSetData?.materials) return;
    this.materialSetData = {
      ...this.materialSetData,
      materials: this.materialSetData.materials.map(m => {
        const { passthrough } = parseAcceptanceGroupValues(m.group_values || m.material?.group_values);
        return {
          ...m,
          group_values: passthrough,
          material: { ...m.material, group_values: passthrough },
        };
      }),
    };
  }

  deleteAreaCalculation(group: any) {
    const label = group.label?.toLowerCase() || 'area calculation';
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Warning',
        `Are you sure you want to delete this entry?`,
        'Delete',
        'Cancel',
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const existingGroupValues = JSON.parse(this.form.controls['groupValues']?.value || '[]');
        const updatedGroupValues = existingGroupValues.filter(gv => gv?.context !== group.context);
        this.form.controls['groupValues']?.setValue(JSON.stringify(updatedGroupValues));
      }
    });
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    return date.toLocaleDateString('en-US', options).replace(/\s?(AM|PM)/i, (_, p) => ' ' + p.toLowerCase());
  }

  scrollToFirstError() {
    setTimeout(() => {
      const errorEl = this.elRef.nativeElement.querySelector('mat-error, .location-station-error');
      errorEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  checkForErrors(currentField?: string) {
    if (currentField) {
      delete this.errorMessages[currentField];
    }
    this.errorMessages = {
      ...this.errorMessages,
      ...(this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField)),
    };
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
